/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import { formatISODate, formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import {
  BottomSheet,
  CbhIcon,
  Divider,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useState } from "react";

import {
  type ActiveBookingReorderingCohort,
  BOOKING_REORDERING_COHORT,
} from "../../PlacementCandidate/types";
import {
  INTERVIEW_TYPE,
  type InterviewType,
} from "../../WorkerShifts/api/useFetchPaginatedInterviews";
import {
  type InterviewAvailability,
  TransformedAvailabilityTimeslot,
} from "../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../api/useGetPlacement";
import { useRecruiterCallTimeStorage } from "../hooks/useRecruiterCallTimeStorage";
import { InterviewDateSelector } from "./InterviewDateSelector";
import { InterviewTimeSelector } from "./InterviewTimeSelector";

function getInterviewButtonLabel(params: {
  isReschedule: boolean;
  isProspectWorkplace: boolean;
  isPhoneScreen: boolean;
}): string {
  const { isReschedule, isProspectWorkplace, isPhoneScreen } = params;

  if (isProspectWorkplace) {
    return isReschedule ? "Reschedule walk-in" : "Book yourself as a walk-in";
  }

  if (isPhoneScreen) {
    return isReschedule ? "Reschedule phone call" : "Book a pre-interview phone call";
  }

  return isReschedule ? "Reschedule interview" : "Book an in-person interview";
}

interface PlacementDetailBookInterviewDialogProps {
  placementDetails: PlacementDetail;
  isReschedule: boolean;
  modalState: UseModalState;
  interviewAvailability: InterviewAvailability;
  onBookInterview: (time: { start: Date; end: Date }) => void;
  onClose?: () => void;
  isBookingInterview: boolean;
  interviewType?: InterviewType;
  placementId: string;
  bookingReorderingCohort: ActiveBookingReorderingCohort;
  preSelectedInterviewDate?: string;
  source?: string;
}

export function PlacementDetailBookInterviewDialog(props: PlacementDetailBookInterviewDialogProps) {
  const {
    placementDetails,
    isReschedule,
    modalState,
    interviewAvailability,
    onBookInterview,
    onClose,
    isBookingInterview,
    interviewType,
    placementId,
    bookingReorderingCohort,
    preSelectedInterviewDate,
    source,
  } = props;

  const { loadRecruiterCallTime, clearRecruiterCallTime } = useRecruiterCallTimeStorage();

  // Build a dictionary: "Thu, Apr 20" => [{ start: Date, end: Date }, ...]
  const availabilityMap = interviewAvailability.reduce<
    Record<string, Array<TransformedAvailabilityTimeslot>>
  >((accumulator, slot) => {
    const startDate = formatShortDateWithWeekday(slot.start);
    accumulator[startDate] ||= [];
    accumulator[startDate].push(slot);
    return accumulator;
  }, {});

  const availableDates = Object.keys(availabilityMap);
  const isProspectWorkplace = Boolean(placementDetails.workplace.placementSettings?.isProspect);

  const eventContext = {
    placementId: placementDetails.id,
    placementApplicationId: placementDetails.applications?.[0]?.id,
    totalTimeSlots: interviewAvailability.filter((slot) => !slot.isBooked).length,
    totalAvailableDates: Object.values(availabilityMap).filter((slots) =>
      slots.some((slot) => !slot.isBooked)
    ).length,
    isReschedule,
    isProspectWorkplace,
    ...(source ? { source } : {}),
    ...(isDefined(bookingReorderingCohort) && { bookingReorderingCohort }),
  };

  // Restore interview time from localStorage for screen_last cohort users who haven't passed screening
  const shouldRestoreStoredTime =
    placementId && bookingReorderingCohort === BOOKING_REORDERING_COHORT.SCREEN_LAST;

  const storedInterviewTime = shouldRestoreStoredTime
    ? loadRecruiterCallTime(placementId)
    : undefined;

  let restoredTimeData: { date: string; time: { start: Date; end: Date } } | undefined;

  if (isDefined(storedInterviewTime)) {
    const matchingSlot = interviewAvailability.find(
      (slot) =>
        !slot.isBooked &&
        slot.start.getTime() === storedInterviewTime.start.getTime() &&
        slot.end.getTime() === storedInterviewTime.end.getTime()
    );
    if (isDefined(matchingSlot)) {
      restoredTimeData = {
        date: formatShortDateWithWeekday(matchingSlot.start),
        time: matchingSlot,
      };
    } else {
      clearRecruiterCallTime(placementId);
      restoredTimeData = undefined;
    }
  }

  const preSelectedFormattedDate = preSelectedInterviewDate
    ? formatShortDateWithWeekday(preSelectedInterviewDate)
    : undefined;

  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    () =>
      restoredTimeData?.date ??
      (preSelectedFormattedDate && availableDates.includes(preSelectedFormattedDate)
        ? preSelectedFormattedDate
        : availableDates[0])
  );

  const [selectedTime, setSelectedTime] = useState<{ start: Date; end: Date } | undefined>(() => {
    if (restoredTimeData) {
      return restoredTimeData.time;
    }
    return undefined;
  });

  const timeSlots =
    selectedDate && isDefined(availabilityMap[selectedDate]) ? availabilityMap[selectedDate] : [];

  // Track if we should scroll to the selected time (only when restored from localStorage)
  const shouldScrollToSelected = isDefined(restoredTimeData);

  /**
   * Handle when a user clicks on a day in the calendar UI.
   */
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setSelectedTime(undefined);
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_DATE_SELECTED, {
      ...eventContext,
      selectedDate: date,
      selectedDateTimeSlots: availabilityMap[date].filter((slot) => !slot.isBooked).length,
    });
  };

  const shouldDisableDate = (dateKey: string) => {
    if (!isDefined(availabilityMap[dateKey])) {
      return true;
    }

    return availabilityMap[dateKey].length === 0;
  };

  const isInterviewSlotAvailable = interviewAvailability.length > 0;

  // Use the actual interview type if provided (for rescheduling), otherwise fall back to workplace settings
  const isPhoneScreen =
    interviewType === INTERVIEW_TYPE.PHONE_SCREEN ||
    (!interviewType &&
      placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ===
        INTERVIEW_TYPE.PHONE_SCREEN);

  const buttonText = getInterviewButtonLabel({
    isReschedule,
    isProspectWorkplace,
    isPhoneScreen,
  });

  const handleBookInterview = (selectedDate: string, selectedTime: { start: Date; end: Date }) => {
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_BOOKING_SUBMITTED, {
      ...eventContext,
      selectedDate: selectedTime.start ? formatISODate(selectedTime.start) : undefined,
      selectedDateDisplay: selectedDate,
      selectedTimeRange: selectedTime
        ? `${formatTime(selectedTime.start)} - ${formatTime(selectedTime.end)}`
        : undefined,
    });

    onBookInterview(selectedTime);
  };

  const handleClose = () => {
    modalState.closeModal();
    onClose?.();
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleClose} />}
      footer={
        <DialogFooter orientation="vertical">
          {isInterviewSlotAvailable && (
            <>
              <Text variant="body2" align="center">
                {isPhoneScreen
                  ? "By continuing, you agree that we are authorized to contact you for phone call attendance verification via email, SMS, or phone call."
                  : "By continuing, you agree that we are authorized to contact you for the interview attendance confirmation via email, sms or a phone call."}
              </Text>
              <LoadingButton
                fullWidth
                variant="contained"
                size="large"
                isLoading={isBookingInterview}
                disabled={!selectedDate || !selectedTime}
                onClick={async () => {
                  if (!selectedDate || !selectedTime) {
                    return;
                  }

                  handleBookInterview(selectedDate, selectedTime);
                }}
              >
                {buttonText}
              </LoadingButton>
            </>
          )}
        </DialogFooter>
      }
      onClose={handleClose}
    >
      <Box sx={{ px: 5, py: 6 }}>
        {isInterviewSlotAvailable ? (
          <>
            <Text variant="h4" align="left" sx={{ fontWeight: 500, pb: 4 }}>
              {isPhoneScreen
                ? "Choose your call time"
                : `Choose your ${isProspectWorkplace ? "walk-in" : "interview"} time`}
            </Text>
            <InterviewDateSelector
              availableDates={availableDates}
              selectedDate={selectedDate}
              shouldDisableDate={shouldDisableDate}
              onDateChange={handleDateChange}
            />
            <Divider />

            <InterviewTimeSelector
              isSelectionPromoted={false}
              placementDetails={placementDetails}
              timeSlots={timeSlots}
              selectedTime={selectedTime}
              isReschedule={isReschedule}
              shouldScrollToSelected={shouldScrollToSelected}
              onTimeSelect={setSelectedTime}
            />
          </>
        ) : (
          <Stack
            spacing={4}
            sx={{ p: 2, height: "300px" }}
            alignItems="center"
            justifyContent="center"
          >
            <CbhIcon type="info" color={(theme) => theme.palette.warning.main} size="large" />
            <Text semibold variant="h5" align="center">
              No {isProspectWorkplace ? "walk-in" : "interview"} slots are available at this time.
              Please check back later.
            </Text>
            <Text variant="body2" align="center">
              Please note that interview dates after your documents expire won&apos;t be visible.
            </Text>
          </Stack>
        )}
      </Box>
    </BottomSheet>
  );
}
